import React, { lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { MagneticButton } from '../components/animated/MagneticButton';
import { courses } from '../data/courses';
import { Clock, MapPin, TrendingUp, Users, Briefcase, GraduationCap } from 'lucide-react';
export function CoursesPage() {
  const navigate = useNavigate();
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'professional':
        return <Briefcase className="w-5 h-5" data-id="element-742" />;
      case 'career':
        return <TrendingUp className="w-5 h-5" data-id="element-743" />;
      case 'corporate':
        return <Users className="w-5 h-5" data-id="element-744" />;
      default:
        return <GraduationCap className="w-5 h-5" data-id="element-745" />;
    }
  };
  return <div className="flex flex-col min-h-screen bg-gradient-to-b from-amber-50 via-white to-gray-50" data-id="element-746">
      <main className="flex-grow pt-20" data-id="element-747">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden" data-id="element-748">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-blue-50" data-id="element-749" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-750">
            <div className="max-w-4xl mx-auto text-center" data-id="element-751">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-8" data-id="element-752">
                Transform Your
                <br data-id="element-753" />
                <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent" data-id="element-754">
                  Career Journey
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-755">
                Practical, high-impact courses designed to equip you with the
                skills employers value most
              </p>
            </div>
          </div>
        </section>

        {/* Courses Grid */}
        <AnimatedSection className="py-24 bg-white" data-id="element-756">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-757">
            <div className="text-center mb-16" data-id="element-758">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-759">
                Our Courses
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-760">
                Choose from our range of professional development programs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-761">
              {courses.map((course, index) => <div key={course.id} className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-2" onClick={() => navigate(`/courses/${course.id}`)} data-id="element-762">
                  <div className="relative h-64 overflow-hidden" data-id="element-763">
                    <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" style={{
                  filter: 'brightness(0.95) contrast(1.05) saturate(0.9) sepia(0.1)'
                }} data-id="element-764" />
                    <div className="absolute top-4 right-4" data-id="element-765">
                      <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2" data-id="element-766">
                        {getCategoryIcon(course.category)}
                        <span className="text-sm font-semibold text-gray-900 capitalize" data-id="element-767">
                          {course.category}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-8" data-id="element-768">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300" data-id="element-769">
                      {course.title}
                    </h3>
                    <p className="text-lg text-blue-600 font-medium mb-4" data-id="element-770">
                      {course.subtitle}
                    </p>
                    <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-771">
                      {course.description}
                    </p>

                    <div className="flex flex-wrap gap-4 mb-6" data-id="element-772">
                      <div className="flex items-center gap-2 text-gray-600" data-id="element-773">
                        <Clock className="w-5 h-5 text-blue-600" data-id="element-774" />
                        <span className="text-sm" data-id="element-775">{course.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600" data-id="element-776">
                        <MapPin className="w-5 h-5 text-green-600" data-id="element-777" />
                        <span className="text-sm" data-id="element-778">Virtual</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-779">
                      <div data-id="element-780">
                        <div className="text-3xl font-bold text-gray-900" data-id="element-781">
                          {course.price}
                        </div>
                        <div className="text-sm text-gray-600" data-id="element-782">
                          {course.priceUSD}
                        </div>
                      </div>
                      <MagneticButton variant="primary" size="sm" data-id="element-783">
                        Learn More
                      </MagneticButton>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection className="py-32 bg-gradient-to-br from-blue-600 to-green-600 relative overflow-hidden" data-id="element-784">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-785" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-786">
            <div className="max-w-4xl mx-auto text-center" data-id="element-787">
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-8" data-id="element-788">
                Ready to Start Learning?
              </h2>
              <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto" data-id="element-789">
                Join our next cohort and take the first step toward your career
                transformation
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-790">
                <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/courses')} data-id="element-791">
                  Browse All Courses
                </MagneticButton>
                <MagneticButton variant="outline" size="lg" onClick={() => navigate('/contact')} data-id="element-792">
                  Contact Us
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}